// Back-to-front depth sort for alpha-correct splat rendering.
// Counting sort over a density-adaptive depth key (up to 20-bit). Stable O(n).
//
// Sort keys are NOT a uniform quantization of [minZ, maxZ]: a uniform map wastes
// most of its buckets on the empty depth ranges of a scan (dense subject + far,
// sparse background), collapsing hundreds of near splats into one bucket. Splats
// that share a bucket keep arbitrary index order, so transparent layers blend in
// the wrong order and the image looks flat / "oil-paint". Instead we build a
// coarse depth histogram and hand each bucket a share of keys proportional to how
// many splats fall in it — the dense slab gets fine resolution. This mirrors the
// PlayCanvas sort worker (which bins per chunk); we bin per splat since we already
// touch every vz, which is a touch more accurate.

const NBINS = 256;

export class DepthSorter {
  private indicesBuf = new Uint32Array(0);
  private depthsBuf = new Int32Array(0);
  private viewZ = new Float32Array(0);
  private counts = new Uint32Array(1 << 20);  // up to 20-bit adaptive
  private binCount = new Float64Array(NBINS);
  private binBase = new Float64Array(NBINS);
  private binDiv = new Float64Array(NBINS);

  // Throttle: only re-sort if the camera's view direction or position
  // changed by more than these thresholds since the last sort.
  private lastViewKey: Float32Array | null = null;
  private readonly posThresh = 0.05;
  private readonly dirThresh = 0.02;

  // Returns indices ordered back-to-front (largest depth first).
  sort(positions: Float32Array, viewMatrix: Float32Array, count: number): Uint32Array {
    if (this.indicesBuf.length < count) {
      this.indicesBuf = new Uint32Array(count);
      this.depthsBuf = new Int32Array(count);
      this.viewZ = new Float32Array(count);
    }
    const idx = this.indicesBuf.subarray(0, count);
    const keys = this.depthsBuf.subarray(0, count);
    const vzs = this.viewZ.subarray(0, count);

    // view-space z = m02*x + m06*y + m10*z + m14 (row 2 of column-major matrix)
    const m02 = viewMatrix[2]!;
    const m06 = viewMatrix[6]!;
    const m10 = viewMatrix[10]!;
    const m14 = viewMatrix[14]!;

    // One pass over positions: depth and its range together. Reading the
    // 3-float stride twice was the single most expensive thing this class did.
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < count; i++) {
      const b = i * 3;
      const vz = m02 * positions[b]! + m06 * positions[b + 1]! + m10 * positions[b + 2]! + m14;
      vzs[i] = vz;
      if (vz < minZ) minZ = vz;
      if (vz > maxZ) maxZ = vz;
    }
    const bits = Math.max(16, Math.min(20, Math.round(Math.log2(Math.max(count, 16) / 4))));
    const maxKey = (1 << bits) - 1;
    const nbuckets = 1 << bits;
    const range = maxZ - minZ || 1;
    const binRange = range / NBINS;

    // Coarse depth histogram: how many splats fall in each of NBINS depth slices.
    const binCount = this.binCount;
    const binBase = this.binBase;
    const binDiv = this.binDiv;
    binCount.fill(0);
    for (let i = 0; i < count; i++) {
      let bi = ((vzs[i]! - minZ) / binRange) | 0;
      if (bi < 0) bi = 0; else if (bi >= NBINS) bi = NBINS - 1;
      binCount[bi]!++;
    }
    // Give each bin a share of the key space proportional to its splat count, then
    // prefix-sum the shares into a base key per bin. Dense slices get more keys.
    for (let i = 0; i < NBINS; i++) binDiv[i] = ((binCount[i]! / count) * nbuckets) | 0;
    binBase[0] = 0;
    for (let i = 1; i < NBINS; i++) binBase[i] = binBase[i - 1]! + binDiv[i - 1]!;

    // GL convention: vz < 0 in front of camera; more negative = farther. minZ maps
    // to bin 0 → smallest key → drawn first. Ascending counting sort then yields
    // back-to-front order (farthest first → correct premultiplied alpha blending).
    const counts = this.counts;
    counts.fill(0);
    for (let i = 0; i < count; i++) {
      const d = (vzs[i]! - minZ) / binRange;
      let bi = d | 0;
      if (bi < 0) bi = 0; else if (bi >= NBINS) bi = NBINS - 1;
      const frac = d - bi;
      let k = (binBase[bi]! + binDiv[bi]! * frac) | 0;
      if (k < 0) k = 0; else if (k > maxKey) k = maxKey;
      keys[i] = k;
      counts[k]!++;
    }
    let sum = 0;
    for (let i = 0; i < nbuckets; i++) {
      const c = counts[i]!;
      counts[i] = sum;
      sum += c;
    }
    for (let i = 0; i < count; i++) idx[counts[keys[i]!]!++] = i;
    return idx;
  }

  // Returns true if the camera changed enough to warrant a re-sort.
  needsResort(viewMatrix: Float32Array): boolean {
    if (!this.lastViewKey) {
      this.lastViewKey = new Float32Array(viewMatrix);
      return true;
    }
    // Compare row 2 (forward) and translation column.
    const last = this.lastViewKey;
    const dirDelta =
      Math.abs(viewMatrix[2]! - last[2]!) +
      Math.abs(viewMatrix[6]! - last[6]!) +
      Math.abs(viewMatrix[10]! - last[10]!);
    const posDelta =
      Math.abs(viewMatrix[12]! - last[12]!) +
      Math.abs(viewMatrix[13]! - last[13]!) +
      Math.abs(viewMatrix[14]! - last[14]!);
    if (dirDelta > this.dirThresh || posDelta > this.posThresh) {
      this.lastViewKey.set(viewMatrix);
      return true;
    }
    return false;
  }

  invalidate(): void {
    this.lastViewKey = null;
  }
}
